import { createTheme, MantineColorsTuple } from "@mantine/core";

// Diaspora Modern — Ogarocious brand palette (preview)
// Terracotta signature + warm base + pillar accents

// Terracotta — signature brand color (replaces "orange")
const orange: MantineColorsTuple = [
  "#fbeee6",
  "#f4d5c0",
  "#ebb99a",
  "#e19c74",
  "#da7f4e",
  "#d4552b", // orange.5 — TERRACOTTA (signature/primary CTA)
  "#b84421",
  "#952f0f",
  "#732208",
  "#4f1500",
];

// Slate Teal — Tech pillar accent (replaces "teal")
const teal: MantineColorsTuple = [
  "#eaefef",
  "#cddad9",
  "#adbfbf",
  "#8ba5a5",
  "#7ba4a5", // teal.4 — Tech pillar accent
  "#669090",
  "#4e7676", // teal.6 — primary shade
  "#3b5d5d",
  "#2a4545",
  "#1b2e2e",
];

// Ochre — Dance pillar (Angolan gold, diasporic warmth)
const ochre: MantineColorsTuple = [
  "#faf1e0",
  "#efdcb3",
  "#e4c586",
  "#d8ae5a",
  "#c99647",
  "#b8843d", // ochre.5 — Dance pillar accent
  "#996d31",
  "#795525",
  "#5a3f1a",
  "#3d2a10",
];

// Sage — Think pillar (deep reflective forest)
const sage: MantineColorsTuple = [
  "#e8eee9",
  "#c7d3c9",
  "#a4b6a7",
  "#7e9982",
  "#5f7d64",
  "#4a6b4e", // sage.5 — Think pillar accent
  "#3a563c",
  "#2c422e",
  "#1f2f21",
  "#131e14",
];

// Coral flare — Media pillar (expressive camera warmth)
const coral: MantineColorsTuple = [
  "#fdeae4",
  "#fbcdc0",
  "#f5aa96",
  "#ef886c",
  "#eb6f4f",
  "#e85d3c", // coral.5 — Media pillar accent
  "#c94826",
  "#a53617",
  "#7d270e",
  "#521806",
];

// Warm noir — replaces "dark"
const dark: MantineColorsTuple = [
  "#f5eee0", // dark.0 — warm ivory (primary text)
  "#d8d1c2",
  "#b5ae9f",
  "#8f8878",
  "#6b6557",
  "#4b4638",
  "#312d23",
  "#1f1b15",
  "#1a1410", // dark.8 — surface
  "#0f0b08", // dark.9 — deep espresso base
];

export const theme = createTheme({
  primaryColor: "orange", // terracotta as primary
  primaryShade: 5,
  fontFamily:
    'Geist, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  headings: {
    fontFamily:
      'Jost, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    sizes: {
      h1: {
        fontSize: "clamp(2rem, 5vw, 3.5rem)",
        lineHeight: "1.15",
        fontWeight: "800",
      },
      h2: {
        fontSize: "clamp(1.75rem, 4vw, 2.75rem)",
        lineHeight: "1.2",
        fontWeight: "700",
      },
      h3: {
        fontSize: "clamp(1.375rem, 3vw, 2rem)",
        lineHeight: "1.3",
        fontWeight: "700",
      },
      h4: {
        fontSize: "clamp(1.125rem, 2.5vw, 1.625rem)",
        lineHeight: "1.35",
        fontWeight: "600",
      },
      h5: {
        fontSize: "clamp(1rem, 2vw, 1.375rem)",
        lineHeight: "1.4",
        fontWeight: "600",
      },
      h6: {
        fontSize: "clamp(0.875rem, 1.5vw, 1.125rem)",
        lineHeight: "1.5",
        fontWeight: "600",
      },
    },
  },
  colors: {
    teal,
    orange,
    ochre,
    sage,
    coral,
    dark,
  },
  defaultRadius: "md",
  spacing: {
    xs: "0.5rem",
    sm: "0.75rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
    // Section-level vertical rhythm — used for gaps between top-level
    // page sections (hero → pillars → featured, etc.). Do not use for
    // in-component spacing; keep those on xs/sm/md/lg/xl.
    xxl: "3rem",
    xxxl: "4.5rem",
    xxxxl: "6rem",
  },
  shadows: {
    xs: "0 1px 3px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.3)",
    sm: "0 3px 8px rgba(0,0,0,0.25), 0 2px 4px rgba(0,0,0,0.3)",
    md: "0 8px 24px rgba(0,0,0,0.3), 0 4px 8px rgba(0,0,0,0.25)",
    lg: "0 16px 32px rgba(0,0,0,0.35), 0 8px 16px rgba(0,0,0,0.25)",
    xl: "0 24px 48px rgba(0,0,0,0.4), 0 12px 24px rgba(0,0,0,0.3)",
  },
  other: {
    // Page background — warm espresso base (lifted to dark.8 for comfort)
    // Was #0a0705 → #0f0b08 → #0a0705 (dark.9). Lifted 2026-07-23 after
    // legibility feedback — near-black caused eye strain; dark.8 preserves
    // the warm noir feel while sitting in the industry sweet spot for
    // comfortable dark UI (GitHub ~#1a1a1a, Linear ~#010102 outlier).
    backgroundColor:
      "linear-gradient(180deg, #1a1410 0%, #1f1b15 50%, #1a1410 100%)",

    // Text — warm ivory system (5:1+ contrast site-wide)
    textPrimary: "#f5eee0",
    textSecondary: "rgba(245, 238, 224, 0.90)",
    textDimmed: "rgba(245, 238, 224, 0.72)",

    // Accessibility tokens (WCAG AA — see docs/typography-audit-2026-07-07.md)
    placeholderText: "rgba(245, 238, 224, 0.55)",
    focusRingColor: "#d4552b",
    focusRing: "0 0 0 2px #d4552b",
    errorText: "#c94826", // coral.6 — warm brand-aligned error
    pillarThinkAccessible: "#7e9982", // sage.3 — text-safe variant of pillarThink

    // Signature
    signatureTerracotta: "#d4552b",

    // Pillar accents
    pillarTech: "#7ba4a5",
    pillarDance: "#b8843d",
    pillarMedia: "#e85d3c",
    pillarThink: "#4a6b4e",

    // Legacy compat (mapped to new palette)
    tealDeep: "#4e7676",
    tealMid: "#7ba4a5",
    coralOrange: "#d4552b",
    amberOrange: "#b8843d",

    // Gradients
    primaryGradient:
      "linear-gradient(135deg, #4e7676 0%, #0f0b08 50%, #d4552b 100%)",
    tealToCoralgradient:
      "linear-gradient(135deg, #4e7676 0%, #d4552b 100%)",
    tealToAmberGradient:
      "linear-gradient(135deg, #4e7676 0%, #b8843d 100%)",

    // Glows
    glowTeal: "rgba(78, 118, 118, 0.24)",
    glowTealMid: "rgba(123, 164, 165, 0.20)",
    glowOrange: "rgba(212, 85, 43, 0.22)",
    glowTerracotta: "rgba(212, 85, 43, 0.22)",
    glowOchre: "rgba(184, 132, 61, 0.22)",
    glowSage: "rgba(74, 107, 78, 0.22)",

    // Glassmorphism — warm espresso glass. Compensated for the lifted
    // base 2026-07-23: previous values (rgba(26,20,16,...)) merged with
    // the new #1a1410 bg. Lifted to a warmer, more opaque tint so cards
    // continue to read as elevated surfaces above the page.
    glassBg: "rgba(49, 45, 35, 0.65)",
    glassBgHover: "rgba(59, 52, 40, 0.78)",
    glassBorder: "rgba(212, 85, 43, 0.12)",
    glassBorderHover: "rgba(212, 85, 43, 0.28)",
    glassBlur: "blur(20px) saturate(180%)",
    glassInnerGlow: "inset 0 1px 0 rgba(245,238,224,0.06)",

    // Legacy compat
    borderColor: "rgba(212, 85, 43, 0.12)",
    cardBackground: "rgba(49, 45, 35, 0.65)", // matches lifted glassBg
    accentOrange: "#d4552b",
    accentTeal: "#7ba4a5",
  },
  components: {
    Button: {
      defaultProps: {
        gradient: { from: "orange.5", to: "orange.7", deg: 135 },
      },
    },
  },
});
